import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  PlusIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  EnvelopeIcon,
  ClipboardIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '@/services/supabase';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import SearchableSelect from '@/components/common/SearchableSelect';
import { formatDate, formatPhoneInput } from '@/utils/formatters';
import {
  getIndustries,
  getProfessionsForIndustry,
  getFocusTypesForProfession,
  professionHasFocusTypes,
  buildIndustryDisplayString,
  getAllProfessionNames,
  getIndustriesForProfession,
} from '@/utils/industryTaxonomy';
import toast from 'react-hot-toast';

interface PresetProfile {
  business_id: string;
  business_name: string;
  industry: string;
  industry_category?: string;
  profession?: string;
  focus_types?: string[];
  contact_email: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  website?: string;
  location_type?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  coverage_type?: string;
  coverage_details?: string;
  coverage_radius?: number;
  claim_status: string;
  claimed_at: string | null;
  preset_created_at: string;
  admin_tags: string[] | null;
  email_status?: string;
  invite_clicks?: number;
  claim_code?: string;  // Changed from access_code
  claim_code_expires_at?: string | null;  // Expiration date for the claim code
  claim_code_status?: 'active' | 'attempted' | 'expired' | 'claimed';  // Status: active, attempted, expired, or claimed
  claim_code_attempt_count?: number;
  claim_code_attempted_at?: string | null;
  is_active?: boolean;
  batch_label?: string;
}

interface EmailCampaign {
  id: string;
  business_id: string;
  campaign_type: string;
  status: string;
  sent_at: string;
}

export default function PresetProfiles() {
  const createFormIds = {
    businessName: 'create-business-name',
    profession: 'create-profession',
    industryCategory: 'create-industry-category',
    contactEmail: 'create-contact-email',
    address: 'create-address',
    city: 'create-city',
    state: 'create-state',
    zipCode: 'create-zip-code',
    phone: 'create-phone',
    locationType: 'create-location-type',
    website: 'create-website',
    coverageType: 'create-coverage-type',
    coverageRadius: 'create-coverage-radius',
    coverageDetails: 'create-coverage-details',
    description: 'create-description',
    isActive: 'create-is-active',
  } as const;

  const editFormIds = {
    businessName: 'edit-business-name',
    profession: 'edit-profession',
    industryCategory: 'edit-industry-category',
    contactEmail: 'edit-contact-email',
    address: 'edit-address',
    city: 'edit-city',
    state: 'edit-state',
    zipCode: 'edit-zip-code',
    phone: 'edit-phone',
    locationType: 'edit-location-type',
    website: 'edit-website',
    coverageType: 'edit-coverage-type',
    coverageRadius: 'edit-coverage-radius',
    coverageDetails: 'edit-coverage-details',
    description: 'edit-description',
  } as const;

  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [filter, setFilter] = useState<'all' | 'unclaimed' | 'claimed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteStep, setDeleteStep] = useState<'check' | 'warning' | 'confirm'>('check');
  const [deleteError, setDeleteError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Bulk import progress state
  const [importProgress, setImportProgress] = useState<{
    isProcessing: boolean;
    jobId: string | null;
    progress: number;
    processedCount: number;
    totalCount: number;
    successCount: number;
    errorCount: number;
  }>({
    isProcessing: false,
    jobId: null,
    progress: 0,
    processedCount: 0,
    totalCount: 0,
    successCount: 0,
    errorCount: 0,
  });

  // Email confirmation state (for activation with emails)
  const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
  const [emailConfirmPassword, setEmailConfirmPassword] = useState('');
  const [emailConfirmError, setEmailConfirmError] = useState('');
  const [emailConfirmLoading, setEmailConfirmLoading] = useState(false);
  const [pendingActivationIds, setPendingActivationIds] = useState<string[]>([]);
  const [duplicateConflict, setDuplicateConflict] = useState<{
    duplicates: { business_id: string; business_name: string; contact_email: string; claim_status: string }[];
  } | null>(null);

  // Email toggle - persisted in localStorage
  const [sendEmailOnActivation, setSendEmailOnActivation] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('presetProfiles_sendEmailOnActivation');
      return saved === 'true';
    }
    return false;
  });

  // Form state for single create
  const [formData, setFormData] = useState({
    business_name: '',
    industry_category: '',
    profession: '',
    focus_types: [] as string[],  // Array of selected focus types
    contact_email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    website: '',
    location_types: [] as string[],  // Array of selected location types
    coverage_type: '',
    coverage_details: '',
    coverage_radius: '10',
    description: '',
    is_active: false,  // Create as active or inactive (default: inactive)
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.business_name.trim()) {
      errors.business_name = 'Business name is required';
    }
    if (!formData.industry_category) {
      errors.industry_category = 'Industry category is required';
    }
    if (!formData.profession) {
      errors.profession = 'Profession is required';
    }
    if (!formData.contact_email.trim()) {
      errors.contact_email = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email.trim())) {
      errors.contact_email = 'Invalid email format (e.g. name@example.com)';
    }

    // Optional field format checks
    if (formData.phone.trim() && !/^[\d\s()+-]{7,20}$/.test(formData.phone.trim())) {
      errors.phone = 'Invalid phone number format';
    }
    if (formData.zip_code.trim() && !/^\d{5}(-\d{4})?$/.test(formData.zip_code.trim())) {
      errors.zip_code = 'Invalid ZIP code (e.g. 07307 or 07307-1234)';
    }
    if (formData.state.trim() && !/^[A-Za-z]{2}$/.test(formData.state.trim())) {
      errors.state = 'Use 2-letter state code (e.g. NJ)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Clear a specific field error when the user edits that field
  const clearFieldError = (field: string) => {
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Available location types
  const availableLocationTypes = [
    { value: 'storefront', label: 'Storefront' },
    { value: 'office', label: 'Office' },
    { value: 'outbound_service', label: 'Outbound Service' },
  ];

  // Available coverage types
  const availableCoverageTypes = [
    { value: 'local', label: 'Local (radius-based)' },
    { value: 'regional', label: 'Regional (multiple cities/counties)' },
    { value: 'statewide', label: 'Statewide' },
    { value: 'nationwide', label: 'Nationwide' },
  ];

  // Quick-select radius options
  const radiusOptions = [5, 10, 25, 50, 100];

  // Get available options based on selections
  const availableIndustries = useMemo(() => getIndustries(), []);
  const availableProfessions = useMemo(() => getAllProfessionNames(), []);
  const availableFocusTypes = useMemo(
    () =>
      formData.industry_category && formData.profession
        ? getFocusTypesForProfession(formData.industry_category, formData.profession)
        : [],
    [formData.industry_category, formData.profession]
  );
  const hasFocusTypes = useMemo(
    () =>
      formData.industry_category &&
      formData.profession &&
      professionHasFocusTypes(formData.industry_category, formData.profession),
    [formData.industry_category, formData.profession]
  );

  // Bulk upload state
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvErrors, setCsvErrors] = useState<{ row: number; errors: string[] }[]>([]);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<PresetProfile | null>(null);
  const [editFormData, setEditFormData] = useState({
    business_name: '',
    industry_category: '',
    profession: '',
    focus_types: [] as string[],
    contact_email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    website: '',
    location_types: [] as string[],
    coverage_type: '',
    coverage_details: '',
    coverage_radius: '10',
    description: '',
  });

  // Edit form dropdown options (must be after editFormData state declaration)
  const editAvailableProfessions = useMemo(() => getAllProfessionNames(), []);
  const editAvailableFocusTypes = useMemo(
    () =>
      editFormData.industry_category && editFormData.profession
        ? getFocusTypesForProfession(editFormData.industry_category, editFormData.profession)
        : [],
    [editFormData.industry_category, editFormData.profession]
  );
  const editHasFocusTypes = useMemo(
    () =>
      editFormData.industry_category &&
      editFormData.profession &&
      professionHasFocusTypes(editFormData.industry_category, editFormData.profession),
    [editFormData.industry_category, editFormData.profession]
  );

  const queryClient = useQueryClient();

  // Fetch preset profiles with stats
  const { data, isLoading } = useQuery({
    queryKey: ['presetProfiles', page, filter, searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('business_profiles')
        .select(`
          business_id,
          business_name,
          industry,
          industry_category,
          profession,
          focus_types,
          contact_email,
          address,
          city,
          state,
          zip_code,
          phone,
          website,
          location_type,
          latitude,
          longitude,
          description,
          coverage_type,
          coverage_details,
          coverage_radius,
          claim_status,
          claimed_at,
          preset_created_at,
          admin_tags,
          is_active,
          batch_label,
          claim_codes!claim_codes_preset_business_id_fkey(code, email_sent_at, expires_at, status, attempt_count, attempted_at)
        `, { count: 'exact' })
        .not('preset_created_at', 'is', null)
        .order('preset_created_at', { ascending: false })
        .range((page - 1) * 20, page * 20 - 1);

      if (filter === 'unclaimed') {
        query = query.eq('claim_status', 'unclaimed');
      } else if (filter === 'claimed') {
        query = query.eq('claim_status', 'claimed');
      }

      if (searchTerm.trim()) {
        query = query.ilike('business_name', `%${searchTerm.trim()}%`);
      }

      const { data: profiles, error, count } = await query;
      if (error) throw error;

      // Get invite click counts
      const businessIds = profiles?.map(p => p.business_id) || [];
      const { data: clickCounts } = await supabase
        .from('business_invite_clicks')
        .select('business_id')
        .in('business_id', businessIds);

      const clickCountMap: Record<string, number> = {};
      clickCounts?.forEach(click => {
        clickCountMap[click.business_id] = (clickCountMap[click.business_id] || 0) + 1;
      });

      // Get email statuses
      const { data: emailCampaigns } = await supabase
        .from('business_email_campaigns')
        .select('business_id, status, campaign_type')
        .in('business_id', businessIds)
        .eq('campaign_type', 'welcome')
        .eq('is_test', false);

      const emailStatusMap: Record<string, string> = {};
      emailCampaigns?.forEach(campaign => {
        emailStatusMap[campaign.business_id] = campaign.status;
      });

      // Helper to compute claim code status
      const getClaimCodeStatus = (claimCode: any, claimStatus: string): 'active' | 'attempted' | 'expired' | 'claimed' => {
        // If the profile is claimed, the code status is "claimed"
        if (claimStatus === 'claimed') {
          return 'claimed';
        }
        // If there's an expiration date and it's past, the code is expired
        if (claimCode?.expires_at) {
          const expiresAt = new Date(claimCode.expires_at);
          if (expiresAt < new Date()) {
            return 'expired';
          }
        }
        // If the claim_codes.status is explicitly 'expired', use that
        if (claimCode?.status === 'expired') {
          return 'expired';
        }
        // If the code was attempted (entered but user didn't complete auth)
        if (claimCode?.status === 'attempted') {
          return 'attempted';
        }
        // Otherwise, the code is active
        return 'active';
      };

      return {
        profiles: profiles?.map(p => {
          const claimCode = (p.claim_codes as any)?.[0];
          return {
            ...p,
            claim_code: claimCode?.code || '',
            claim_code_expires_at: claimCode?.expires_at || null,
            claim_code_status: getClaimCodeStatus(claimCode, p.claim_status),
            claim_code_attempt_count: claimCode?.attempt_count || 0,
            claim_code_attempted_at: claimCode?.attempted_at || null,
            email_status: claimCode?.email_sent_at ? 'sent' : 'never_sent',
            invite_clicks: clickCountMap[p.business_id] || 0,
          };
        }) || [],
        total: count || 0,
      };
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['presetProfileStats'],
    queryFn: async () => {
      const { count: totalCount } = await supabase
        .from('business_profiles')
        .select('*', { count: 'exact', head: true })
        .not('preset_created_at', 'is', null);

      const { count: unclaimedCount } = await supabase
        .from('business_profiles')
        .select('*', { count: 'exact', head: true })
        .not('preset_created_at', 'is', null)
        .eq('claim_status', 'unclaimed');

      const total = totalCount || 0;
      const unclaimed = unclaimedCount || 0;
      const claimed = total - unclaimed;

      return {
        total,
        unclaimed,
        claimed,
        claimRate: total ? ((claimed / total) * 100).toFixed(1) : '0',
      };
    },
  });

  // Build request body for create preset profile
  const buildCreateRequestBody = (forceCreate = false) => {
    const industryDisplayString = buildIndustryDisplayString(
      formData.profession,
      formData.focus_types
    );

    return {
      ...formData,
      industry: industryDisplayString, // Legacy field for backward compatibility
      focus_types: formData.focus_types.length > 0 ? formData.focus_types : undefined,
      location_type: formData.location_types.length > 0 ? formData.location_types : undefined,
      coverage_type: formData.location_types.includes('outbound_service') ? formData.coverage_type : undefined,
      coverage_details: formData.location_types.includes('outbound_service') ? formData.coverage_details : undefined,
      coverage_radius: formData.location_types.includes('outbound_service') && formData.coverage_type === 'local'
        ? parseFloat(formData.coverage_radius) || 10
        : undefined,
      is_active: formData.is_active,
      send_email: formData.is_active && sendEmailOnActivation,  // Only send email if creating as active AND toggle is on
      ...(forceCreate ? { force_create: true } : {}),
    };
  };

  // Create single preset profile
  const createMutation = useMutation({
    mutationFn: async (forceCreate: boolean = false) => {
      const { data: session } = await supabase.auth.getSession();

      if (!session?.session?.access_token) {
        throw new Error('Not authenticated. Please log in again.');
      }

      const requestBody = buildCreateRequestBody(forceCreate);

      console.log('Calling admin-create-preset-profile with:', requestBody);

      const response = await supabase.functions.invoke('admin-create-preset-profile', {
        body: requestBody,
      });

      console.log('Response from admin-create-preset-profile:', response);

      if (response.error) {
        // supabase-js FunctionsHttpError: context may be a Response object or parsed JSON
        let errorBody = response.error.context;
        if (errorBody instanceof Response) {
          try {
            errorBody = await errorBody.json();
          } catch {
            errorBody = null;
          }
        }

        // Handle 409 duplicate conflict — show confirmation dialog instead of error
        if (errorBody?.requireConfirmation && errorBody?.duplicates) {
          setDuplicateConflict({ duplicates: errorBody.duplicates });
          return { _duplicateConflict: true };
        }

        const errorMessage = errorBody?.message || response.error.message || 'Failed to call edge function';
        console.error('Edge function error:', errorMessage, errorBody);
        throw new Error(errorMessage);
      }

      if (!response.data) {
        throw new Error('No response data from edge function');
      }

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create profile');
      }

      return response.data;
    },
    onSuccess: (data) => {
      // If duplicate conflict was detected, don't show success — the confirmation dialog handles it
      if (data?._duplicateConflict) return;

      const emailSent = data.email_sent;
      let message = `Profile created with claim code: ${data.claim_code}`;
      if (emailSent) {
        message += ' - Welcome email sent!';
      }
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ['presetProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['presetProfileStats'] });
      setShowCreateModal(false);
      setDuplicateConflict(null);
      setFormData({
        business_name: '',
        industry_category: '',
        profession: '',
        focus_types: [],
        contact_email: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        website: '',
        location_types: [],
        coverage_type: '',
        coverage_details: '',
        coverage_radius: '10',
        description: '',
        is_active: false,  // Reset to inactive
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create profile');
    },
  });

  // Update preset profile
  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingProfile) throw new Error('No profile selected for editing');

      // Build the legacy industry display string
      const industryDisplayString = buildIndustryDisplayString(
        editFormData.profession,
        editFormData.focus_types
      );

      // Parse location_types - handle both string and array formats
      const locationTypes = editFormData.location_types.length > 0
        ? editFormData.location_types
        : null;

      const updateData: Record<string, unknown> = {
        business_name: editFormData.business_name,
        industry: industryDisplayString,
        industry_category: editFormData.industry_category,
        profession: editFormData.profession,
        focus_types: editFormData.focus_types.length > 0 ? editFormData.focus_types : null,
        contact_email: editFormData.contact_email,
        address: editFormData.address || null,
        city: editFormData.city || null,
        state: editFormData.state || null,
        zip_code: editFormData.zip_code || null,
        phone: editFormData.phone || null,
        website: editFormData.website || null,
        location_type: locationTypes ? locationTypes[0] : null, // Primary location type
        description: editFormData.description || null,
      };

      // Add coverage fields if outbound service is selected
      if (editFormData.location_types.includes('outbound_service')) {
        updateData.coverage_type = editFormData.coverage_type || null;
        updateData.coverage_details = editFormData.coverage_details || null;
        updateData.coverage_radius = editFormData.coverage_type === 'local'
          ? parseFloat(editFormData.coverage_radius) || 10
          : null;
      } else {
        updateData.coverage_type = null;
        updateData.coverage_details = null;
        updateData.coverage_radius = null;
      }

      const { error } = await supabase
        .from('business_profiles')
        .update(updateData)
        .eq('business_id', editingProfile.business_id);

      if (error) throw error;

      return { success: true };
    },
    onSuccess: () => {
      toast.success('Profile updated successfully');
      queryClient.invalidateQueries({ queryKey: ['presetProfiles'] });
      setShowEditModal(false);
      setEditingProfile(null);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update profile');
    },
  });

  // Bulk create profiles - uses chunked processing with polling for large datasets
  const bulkCreateMutation = useMutation({
    mutationFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const authToken = session?.session?.access_token;

      // Step 1: Start the import job (validates profiles and creates job record)
      const startResponse = await supabase.functions.invoke('admin-bulk-create-preset-profiles', {
        body: {
          profiles: csvData,
          send_email: sendEmailOnActivation,
        },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Handle error response - Supabase puts non-2xx response body in error.context
      if (startResponse.error) {
        // For FunctionsHttpError, context is a Response object that needs to be parsed
        let errorData = startResponse.data;
        if (startResponse.error.context && typeof startResponse.error.context.json === 'function') {
          try {
            errorData = await startResponse.error.context.json();
          } catch {
            // If parsing fails, fall back to data
          }
        }
        if (errorData?.validation_errors) {
          setCsvErrors(errorData.validation_errors);
          throw new Error(errorData.message || 'Validation errors found');
        }
        throw new Error(errorData?.message || startResponse.error.message || 'Failed to start import');
      }
      if (!startResponse.data?.success) {
        if (startResponse.data?.validation_errors) {
          setCsvErrors(startResponse.data.validation_errors);
          throw new Error(startResponse.data.message || 'Validation errors found');
        }
        throw new Error(startResponse.data?.message || 'Failed to start import');
      }

      const jobId = startResponse.data.job_id;
      const totalCount = startResponse.data.total_count;

      // Initialize progress state
      setImportProgress({
        isProcessing: true,
        jobId,
        progress: 0,
        processedCount: 0,
        totalCount,
        successCount: 0,
        errorCount: 0,
      });

      // Step 2: Poll the process endpoint until complete
      let complete = false;
      let finalData: any = null;

      while (!complete) {
        const processResponse = await supabase.functions.invoke('admin-process-preset-import', {
          body: {
            job_id: jobId,
            chunk_size: 50,
          },
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (processResponse.error) {
          // For FunctionsHttpError, context is a Response object that needs to be parsed
          let errorData = processResponse.data;
          if (processResponse.error.context && typeof processResponse.error.context.json === 'function') {
            try {
              errorData = await processResponse.error.context.json();
            } catch {
              // If parsing fails, fall back to data
            }
          }
          throw new Error(errorData?.message || processResponse.error.message || 'Processing failed');
        }

        const data = processResponse.data;

        if (!data.success) {
          throw new Error(data.message || 'Processing failed');
        }

        // Update progress
        setImportProgress(prev => ({
          ...prev,
          progress: data.progress,
          processedCount: data.processed_count,
          successCount: data.success_count,
          errorCount: data.error_count,
        }));

        if (data.complete) {
          complete = true;
          finalData = data;
        } else {
          // Small delay between chunks to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Reset progress state
      setImportProgress({
        isProcessing: false,
        jobId: null,
        progress: 0,
        processedCount: 0,
        totalCount: 0,
        successCount: 0,
        errorCount: 0,
      });

      return finalData;
    },
    onSuccess: (data) => {
      let message = `Created ${data.success_count} profiles`;
      if (data.active_count > 0) {
        message += ` (${data.active_count} active)`;
      }
      if (data.emails_sent > 0) {
        message += ` - ${data.emails_sent} welcome email(s) sent!`;
      }
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ['presetProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['presetProfileStats'] });
      setShowBulkUploadModal(false);
      setCsvData([]);
      setCsvErrors([]);

      // Download results CSV
      if (data.results?.length > 0) {
        downloadResultsCsv(data.results);
      }
    },
    onError: (error) => {
      // Reset progress on error
      setImportProgress({
        isProcessing: false,
        jobId: null,
        progress: 0,
        processedCount: 0,
        totalCount: 0,
        successCount: 0,
        errorCount: 0,
      });

      // Always show error toast with the actual error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to create profiles';
      toast.error(errorMessage);
    },
  });

  // Send welcome emails (one by one via admin-send-welcome-email)
  const sendEmailMutation = useMutation({
    mutationFn: async (businessIds: string[]) => {
      // Fetch profiles with their claim codes for the selected businesses
      const { data: profiles, error: fetchError } = await supabase
        .from('business_profiles')
        .select(`
          business_id,
          contact_email,
          business_name,
          description,
          claim_codes!claim_codes_preset_business_id_fkey(code, claim_url)
        `)
        .in('business_id', businessIds);

      if (fetchError || !profiles) {
        throw new Error('Failed to fetch profile details for email sending');
      }

      let sent = 0;
      let failed = 0;

      const results = await Promise.allSettled(
        profiles.map(async (profile) => {
          const claimCodeData = Array.isArray(profile.claim_codes)
            ? profile.claim_codes[0]
            : profile.claim_codes;
          if (!claimCodeData?.code) return;

          const recipientEmail = testMode && testEmail ? testEmail : profile.contact_email;
          const claimUrl = claimCodeData.claim_url || `https://www.linkedbysix.com/claim/${claimCodeData.code}`;

          const emailResponse = await supabase.functions.invoke('admin-send-welcome-email', {
            body: {
              business_id: profile.business_id,
              recipient_email: recipientEmail,
              business_name: profile.business_name,
              claim_code: claimCodeData.code,
              claim_url: claimUrl,
              business_description: profile.description || '',
            },
          });

          if (emailResponse.data?.success) {
            sent++;
          } else {
            failed++;
            console.error(`Failed to send email for ${profile.business_name}:`, emailResponse.error);
          }
        })
      );

      return { sent, failed, total: profiles.length };
    },
    onSuccess: (data) => {
      let message = `Sent ${data.sent} email${data.sent !== 1 ? 's' : ''}`;
      if (data.failed > 0) {
        message += ` (${data.failed} failed)`;
      }
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ['presetProfiles'] });
      setShowEmailModal(false);
      setSelectedProfiles([]);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to send emails');
    },
  });

  // Bulk activate profiles (make visible in consumer search)
  // Note: Database trigger sync_is_active_to_status automatically syncs status/is_searchable
  const bulkActivateMutation = useMutation({
    mutationFn: async (businessIds: string[]) => {
      // First, get profiles that haven't received an email yet (first-time activations)
      let firstTimeActivations: Array<{
        business_id: string;
        contact_email: string;
        business_name: string;
        description: string;
        claim_code: string;
      }> = [];

      if (sendEmailOnActivation) {
        // Query for profiles that haven't received an email yet
        const { data: profilesWithClaimCodes } = await supabase
          .from('business_profiles')
          .select(`
            business_id,
            contact_email,
            business_name,
            description,
            claim_codes!claim_codes_preset_business_id_fkey(code, email_sent_at)
          `)
          .in('business_id', businessIds)
          .eq('claim_status', 'unclaimed');

        // Filter to only those that haven't received email yet
        firstTimeActivations = (profilesWithClaimCodes || [])
          .filter(p => {
            const claimCode = (p.claim_codes as any)?.[0];
            return claimCode && !claimCode.email_sent_at && p.contact_email;
          })
          .map(p => ({
            business_id: p.business_id,
            contact_email: p.contact_email,
            business_name: p.business_name,
            description: p.description || '',
            claim_code: (p.claim_codes as any)?.[0]?.code,
          }));
      }

      // Update activation status
      // Include status and is_searchable to ensure v_active_businesses view includes them
      const { error } = await supabase
        .from('business_profiles')
        .update({
          is_active: true,
          business_status: 'Active',
          status: 'active',  // Required for v_active_businesses view
          is_searchable: true,  // Explicitly enable search visibility
          updated_at: new Date().toISOString(),
        })
        .in('business_id', businessIds);

      if (error) throw error;

      // Refresh search documents for activated profiles
      // This ensures they appear in search results immediately after activation
      let searchDocsRefreshed = 0;
      try {
        const { data: refreshResult, error: refreshError } = await supabase
          .rpc('refresh_search_docs_batch', { p_business_ids: businessIds });

        if (!refreshError && refreshResult) {
          searchDocsRefreshed = Array.isArray(refreshResult)
            ? refreshResult.filter((r: { refreshed: boolean }) => r.refreshed).length
            : businessIds.length;
        }
      } catch (refreshErr) {
        console.error('Failed to refresh search docs:', refreshErr);
        // Don't fail activation, search docs will sync eventually
      }

      // If email toggle is on, send emails to first-time activations
      let emailsSent = 0;
      if (sendEmailOnActivation && firstTimeActivations.length > 0) {
        const emailResults = await Promise.allSettled(
          firstTimeActivations.map(async (profile) => {
            const claimUrl = `https://www.linkedbysix.com/claim/${profile.claim_code}`;
            const emailResponse = await supabase.functions.invoke('admin-send-welcome-email', {
              body: {
                business_id: profile.business_id,
                recipient_email: profile.contact_email,
                business_name: profile.business_name,
                claim_code: profile.claim_code,
                claim_url: claimUrl,
                business_description: profile.description || '',
              },
            });

            if (emailResponse.data?.success) {
              // Update claim_codes.email_sent_at
              await supabase
                .from('claim_codes')
                .update({ email_sent_at: new Date().toISOString() })
                .eq('preset_business_id', profile.business_id);
              return true;
            }
            return false;
          })
        );

        emailsSent = emailResults.filter(r => r.status === 'fulfilled' && r.value === true).length;
      }

      return { activated: businessIds.length, emailsSent, searchDocsRefreshed };
    },
    onSuccess: (data) => {
      let message = `Activated ${data.activated} profile${data.activated !== 1 ? 's' : ''}`;
      if (sendEmailOnActivation && data.emailsSent > 0) {
        message += ` - ${data.emailsSent} welcome email${data.emailsSent !== 1 ? 's' : ''} sent`;
      }
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ['presetProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['presetProfileStats'] });
      setSelectedProfiles([]);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to activate profiles');
    },
  });

  // Bulk deactivate profiles (hide from consumer search)
  // Note: Database trigger sync_is_active_to_status automatically syncs status/is_searchable
  const bulkDeactivateMutation = useMutation({
    mutationFn: async (businessIds: string[]) => {
      const { error } = await supabase
        .from('business_profiles')
        .update({
          is_active: false,
          business_status: 'Not Active',
          updated_at: new Date().toISOString(),
        })
        .in('business_id', businessIds);

      if (error) throw error;
      return { count: businessIds.length };
    },
    onSuccess: (data) => {
      toast.success(`Deactivated ${data.count} profile${data.count !== 1 ? 's' : ''}`);
      queryClient.invalidateQueries({ queryKey: ['presetProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['presetProfileStats'] });
      setSelectedProfiles([]);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to deactivate profiles');
    },
  });

  // Delete preset profiles mutation - calls edge function that handles Neo4j cleanup
  const deleteMutation = useMutation({
    mutationFn: async ({ businessIds, password }: { businessIds: string[]; password: string }) => {
      const response = await supabase.functions.invoke('admin-delete-preset-profiles', {
        body: {
          business_ids: businessIds,
          password: password,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to call delete function');
      }

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to delete profiles');
      }

      return {
        count: response.data.supabase_deleted,
        neo4jDeleted: response.data.neo4j_deleted,
        results: response.data.results,
      };
    },
    onSuccess: (data) => {
      const neo4jMsg = data.neo4jDeleted > 0 ? ` (${data.neo4jDeleted} removed from Neo4j)` : '';
      toast.success(`Deleted ${data.count} preset profile${data.count !== 1 ? 's' : ''}${neo4jMsg}`);
      queryClient.invalidateQueries({ queryKey: ['presetProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['presetProfileStats'] });
      setSelectedProfiles([]);
      setShowDeleteModal(false);
      setDeletePassword('');
      setDeleteStep('check');
      setDeleteError('');
    },
    onError: (error) => {
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete profiles');
    },
  });

  // Helper function to check if delete is allowed
  const getDeleteValidation = () => {
    const selectedProfileData = data?.profiles.filter(p => selectedProfiles.includes(p.business_id)) || [];
    const claimedProfiles = selectedProfileData.filter(p => p.claim_status === 'claimed');
    const emailSentProfiles = selectedProfileData.filter(p => p.email_status === 'sent');

    return {
      canDelete: claimedProfiles.length === 0,
      claimedCount: claimedProfiles.length,
      emailSentCount: emailSentProfiles.length,
      selectedProfileData,
    };
  };

  // Handle delete button click
  const handleDeleteClick = () => {
    const validation = getDeleteValidation();
    if (!validation.canDelete) {
      setDeleteStep('check');
    } else if (validation.emailSentCount > 0) {
      setDeleteStep('warning');
    } else {
      setDeleteStep('confirm');
    }
    setDeleteError('');
    setDeletePassword('');
    setShowDeleteModal(true);
  };

  // Get info about profiles that will receive emails on activation
  const getEmailActivationInfo = (businessIds: string[]) => {
    const selectedProfileData = data?.profiles.filter(p => businessIds.includes(p.business_id)) || [];
    // Count unclaimed profiles without email_sent_at (will receive email)
    const willReceiveEmail = selectedProfileData.filter(p => {
      const claimCode = (p as any).claim_codes?.[0];
      return p.claim_status === 'unclaimed' && claimCode && !claimCode.email_sent_at && p.contact_email;
    });
    return {
      totalToActivate: businessIds.length,
      willReceiveEmailCount: willReceiveEmail.length,
      willReceiveEmail,
    };
  };

  // Handle activate button click - show confirmation if emails will be sent
  const handleActivateClick = (businessIds: string[]) => {
    if (sendEmailOnActivation) {
      const info = getEmailActivationInfo(businessIds);
      if (info.willReceiveEmailCount > 0) {
        // Show confirmation modal
        setPendingActivationIds(businessIds);
        setEmailConfirmPassword('');
        setEmailConfirmError('');
        setShowEmailConfirmModal(true);
        return;
      }
    }
    // No emails to send, proceed directly
    bulkActivateMutation.mutate(businessIds);
  };

  // Verify password and proceed with activation + emails
  const handleEmailConfirmedActivation = async () => {
    if (!emailConfirmPassword) {
      setEmailConfirmError('Password is required');
      return;
    }

    setEmailConfirmLoading(true);
    setEmailConfirmError('');

    try {
      // Verify password by attempting to sign in with current user's email
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        setEmailConfirmError('Could not verify user session');
        setEmailConfirmLoading(false);
        return;
      }

      // Verify password by re-authenticating
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: emailConfirmPassword,
      });

      if (authError) {
        setEmailConfirmError('Incorrect password');
        setEmailConfirmLoading(false);
        return;
      }

      // Password verified, proceed with activation (with emails)
      setShowEmailConfirmModal(false);
      setEmailConfirmPassword('');
      bulkActivateMutation.mutate(pendingActivationIds);
    } catch (err) {
      setEmailConfirmError('Failed to verify password');
    } finally {
      setEmailConfirmLoading(false);
    }
  };

  // Activate without sending emails (bypass email toggle for this action)
  const handleActivateWithoutEmails = () => {
    setShowEmailConfirmModal(false);
    setEmailConfirmPassword('');

    // Temporarily disable email sending for this activation
    const originalToggle = sendEmailOnActivation;
    setSendEmailOnActivation(false);

    // Use a modified mutation that doesn't send emails
    // We'll call the mutation directly but the sendEmailOnActivation state will be false
    bulkActivateMutation.mutate(pendingActivationIds, {
      onSettled: () => {
        // Restore the toggle after mutation completes
        setSendEmailOnActivation(originalToggle);
        localStorage.setItem('presetProfiles_sendEmailOnActivation', originalToggle.toString());
      }
    });
  };

  // Download CSV template
  const downloadTemplate = () => {
    // Required: business_name, industry_category, contact_email
    // Optional: profession, focus_types, address, city, state, zip_code, phone, website, location_type, latitude, longitude, description, is_active
    // Note: focus_types uses semi-colon separator for multiple values (e.g., "New Vehicles;Used Vehicles")
    // Note: is_active accepts 'true', 'yes', '1', or 'active' for active status (defaults to inactive)
    const headers = 'business_name,industry_category,profession,focus_types,contact_email,address,city,state,zip_code,phone,website,location_type,coverage_type,coverage_radius,coverage_details,latitude,longitude,description,is_active';
    const example = 'Acme Auto Sales,Automotive Services,Auto Sales,New Vehicles;Used Vehicles,owner@acmeauto.com,123 Main St,Austin,TX,78701,512-555-1234,https://www.acmeauto.com,storefront,,,,30.2672,-97.7431,Professional auto sales,false';
    const validValues = [
      '# VALID VALUES REFERENCE (delete these comment rows before uploading):',
      `# industry_category: ${getIndustries().join(' | ')}`,
      '# location_type: storefront | office | outbound_service',
      '# coverage_type: local | regional | statewide | nationwide (only when location_type = outbound_service)',
      '# coverage_radius: number in miles (only when coverage_type = local)',
      '# coverage_details: free text describing service area (optional)',
      '# is_active: true/yes/1/active (anything else = inactive)',
      '# focus_types: use semi-colon separator for multiple values. Values must match the profession within the industry taxonomy.',
      '# profession: must match a valid profession for the chosen industry_category. See admin console taxonomy for full list.',
    ].join('\n');

    const csv = headers + '\n' + example + '\n' + validValues;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'preset_profiles_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Download results CSV
  const downloadResultsCsv = (results: any[]) => {
    const headers = 'business_name,claim_code,status,is_active,geocoded,latitude,longitude,email_sent,error';
    const rows = results.map(r =>
      `"${r.business_name}","${r.claim_code || ''}","${r.success ? 'created' : 'failed'}","${r.is_active ? 'active' : 'inactive'}","${r.geocoded ? 'yes' : 'no'}","${r.coordinates?.latitude || ''}","${r.coordinates?.longitude || ''}","${r.email_sent ? 'yes' : 'no'}","${r.error || ''}"`
    ).join('\n');

    const csv = headers + '\n' + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'preset_profiles_results.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Parse CSV file
  // Parse a CSV line properly handling quoted fields with commas
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          // Toggle quote mode
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    // Don't forget the last field
    result.push(current.trim());
    return result;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());

      const data = lines.slice(1).map((line, index) => {
        const values = parseCSVLine(line);
        const row: Record<string, string | number | string[] | boolean | undefined> = {};
        headers.forEach((header, i) => {
          const value = values[i] || '';
          // Convert latitude, longitude, and coverage_radius to numbers if present
          if (header === 'latitude' || header === 'longitude' || header === 'coverage_radius') {
            const num = parseFloat(value);
            row[header] = !isNaN(num) ? num : undefined;
          // Convert focus_types to array (semi-colon separated within CSV cell)
          } else if (header === 'focus_types') {
            // Use semi-colon as separator within the cell since comma is CSV delimiter
            // Always store as array (empty array if no value) to avoid type mismatch downstream
            row[header] = value ? value.split(';').map(ft => ft.trim()).filter(ft => ft) : [];
          // Convert is_active to boolean (accepts 'true', 'yes', '1', 'active')
          } else if (header === 'is_active') {
            const lowerValue = value.toLowerCase();
            row[header] = ['true', 'yes', '1', 'active'].includes(lowerValue);
          } else {
            row[header] = value;
          }
        });
        return row;
      });

      // Validate parsed data against app taxonomy
      const validIndustries = getIndustries();
      const validLocationTypes = ['storefront', 'office', 'outbound_service'];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const errors: { row: number; errors: string[] }[] = [];

      data.forEach((row, index) => {
        const rowErrors: string[] = [];
        const rowNum = index + 2; // +2 because row 1 is header, data starts at row 2

        // Required fields
        if (!row.business_name || !(row.business_name as string).trim()) {
          rowErrors.push('Business name is required');
        }
        const industry = (row.industry_category as string)?.trim() || (row.industry as string)?.trim();
        if (!industry) {
          rowErrors.push('Industry category is required');
        } else if (!validIndustries.some(vi => vi.toLowerCase() === industry.toLowerCase())) {
          rowErrors.push(`Invalid industry_category: "${industry}"`);
        }
        const email = (row.contact_email as string)?.trim();
        if (!email) {
          rowErrors.push('Contact email is required');
        } else if (!emailRegex.test(email)) {
          rowErrors.push(`Invalid email format: "${email}"`);
        }

        // Profession validation (optional but must match taxonomy if provided)
        const profession = (row.profession as string)?.trim();
        if (profession && industry) {
          const matchedIndustry = validIndustries.find(vi => vi.toLowerCase() === industry.toLowerCase());
          if (matchedIndustry) {
            const validProfessions = getProfessionsForIndustry(matchedIndustry);
            if (!validProfessions.some(vp => vp.toLowerCase() === profession.toLowerCase())) {
              rowErrors.push(`Invalid profession "${profession}" for industry "${matchedIndustry}"`);
            }
          }
        }

        // Focus types validation (must match taxonomy for given industry+profession)
        const focusTypes = row.focus_types as string[];
        if (focusTypes && focusTypes.length > 0 && industry && profession) {
          const matchedIndustry = validIndustries.find(vi => vi.toLowerCase() === industry.toLowerCase());
          if (matchedIndustry) {
            const validFocusTypes = getFocusTypesForProfession(matchedIndustry, profession);
            if (validFocusTypes.length === 0) {
              rowErrors.push(`Profession "${profession}" does not support focus_types`);
            } else {
              for (const ft of focusTypes) {
                if (!validFocusTypes.some(vft => vft.toLowerCase() === ft.toLowerCase())) {
                  rowErrors.push(`Invalid focus_type "${ft}" for "${profession}"`);
                }
              }
            }
          }
        }

        // Location type validation (optional but must match valid values if provided)
        const locationType = (row.location_type as string)?.trim();
        if (locationType && !validLocationTypes.includes(locationType.toLowerCase())) {
          rowErrors.push(`Invalid location_type: "${locationType}" (must be: ${validLocationTypes.join(', ')})`);
        }

        // Coverage field validation
        const validCoverageTypes = ['local', 'regional', 'statewide', 'nationwide'];
        const coverageType = (row.coverage_type as string)?.trim();
        const coverageRadius = row.coverage_radius as number | undefined;
        if (coverageType) {
          if (!locationType || locationType.toLowerCase() !== 'outbound_service') {
            rowErrors.push('coverage_type requires location_type to be "outbound_service"');
          } else if (!validCoverageTypes.includes(coverageType.toLowerCase())) {
            rowErrors.push(`Invalid coverage_type: "${coverageType}" (must be: ${validCoverageTypes.join(', ')})`);
          }
        }
        if (coverageRadius !== undefined) {
          if (!coverageType || coverageType.toLowerCase() !== 'local') {
            rowErrors.push('coverage_radius requires coverage_type to be "local"');
          } else if (coverageRadius <= 0) {
            rowErrors.push('coverage_radius must be a positive number');
          }
        }
        if (locationType?.toLowerCase() === 'outbound_service' && !coverageType) {
          rowErrors.push('coverage_type is required when location_type is "outbound_service"');
        }

        if (rowErrors.length > 0) {
          errors.push({ row: rowNum, errors: rowErrors });
        }
      });

      setCsvData(data);
      setCsvErrors(errors);
    };
    reader.readAsText(file);
  };

  // Copy code to clipboard
  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  // Open edit modal and populate form with profile data
  const openEditModal = (profile: PresetProfile) => {
    setEditingProfile(profile);

    // Parse location_type - could be string or array in database
    let locationTypes: string[] = [];
    if (profile.location_type) {
      locationTypes = Array.isArray(profile.location_type)
        ? profile.location_type
        : [profile.location_type];
    }

    setEditFormData({
      business_name: profile.business_name || '',
      industry_category: profile.industry_category || '',
      profession: profile.profession || '',
      focus_types: profile.focus_types || [],
      contact_email: profile.contact_email || '',
      address: profile.address || '',
      city: profile.city || '',
      state: profile.state || '',
      zip_code: profile.zip_code || '',
      phone: formatPhoneInput(profile.phone || ''),
      website: profile.website || '',
      location_types: locationTypes,
      coverage_type: profile.coverage_type || '',
      coverage_details: profile.coverage_details || '',
      coverage_radius: profile.coverage_radius?.toString() || '10',
      description: profile.description || '',
    });
    setShowEditModal(true);
  };

  const columns = useMemo<ColumnDef<PresetProfile>[]>(
    () => [
      // Select checkbox
      {
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            className="rounded border-gray-300"
            aria-label="Select all preset profiles"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            className="rounded border-gray-300"
            aria-label={`Select preset profile ${row.original.business_name}`}
          />
        ),
      },
      // Template fields (in order matching downloadable template)
      {
        accessorKey: 'business_name',
        header: 'Business Name',
        cell: ({ row }) => (
          <div className="font-medium">{row.original.business_name}</div>
        ),
      },
      {
        accessorKey: 'industry',
        header: 'Industry',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.industry || '-'}</span>
        ),
      },
      {
        accessorKey: 'industry_category',
        header: 'Category',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.industry_category || '-'}</span>
        ),
      },
      {
        accessorKey: 'profession',
        header: 'Profession',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.profession || '-'}</span>
        ),
      },
      {
        accessorKey: 'focus_types',
        header: 'Focus Types',
        cell: ({ row }) => {
          const focusTypes = row.original.focus_types;
          if (!focusTypes || focusTypes.length === 0) {
            return <span className="text-gray-400">-</span>;
          }
          return (
            <span className="text-gray-700">{focusTypes.join(', ')}</span>
          );
        },
      },
      {
        accessorKey: 'contact_email',
        header: 'Email',
        cell: ({ row }) => (
          row.original.contact_email ? (
            <a
              href={`mailto:${row.original.contact_email}`}
              className="text-brand hover:underline"
            >
              {row.original.contact_email}
            </a>
          ) : (
            <span className="text-gray-400">-</span>
          )
        ),
      },
      {
        accessorKey: 'address',
        header: 'Address',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.address || '-'}</span>
        ),
      },
      {
        accessorKey: 'city',
        header: 'City',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.city || '-'}</span>
        ),
      },
      {
        accessorKey: 'state',
        header: 'State',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.state || '-'}</span>
        ),
      },
      {
        accessorKey: 'zip_code',
        header: 'Zip',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.zip_code || '-'}</span>
        ),
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.phone || '-'}</span>
        ),
      },
      {
        accessorKey: 'website',
        header: 'Website',
        cell: ({ row }) => (
          row.original.website ? (
            <a
              href={row.original.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:underline truncate max-w-[150px] block"
              title={row.original.website}
            >
              {row.original.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          ) : (
            <span className="text-gray-400">-</span>
          )
        ),
      },
      {
        accessorKey: 'location_type',
        header: 'Location Type',
        cell: ({ row }) => {
          const locationType = row.original.location_type;
          if (!locationType) {
            return <span className="text-gray-400">-</span>;
          }
          const labels: Record<string, string> = {
            storefront: 'Storefront',
            office: 'Office',
            outbound_service: 'Outbound',
          };
          return (
            <Badge variant="gray">
              {labels[locationType] || locationType}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'latitude',
        header: 'Lat',
        cell: ({ row }) => (
          <span className="text-gray-700 text-sm">
            {row.original.latitude != null ? row.original.latitude.toFixed(4) : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'longitude',
        header: 'Lng',
        cell: ({ row }) => (
          <span className="text-gray-700 text-sm">
            {row.original.longitude != null ? row.original.longitude.toFixed(4) : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => {
          const desc = row.original.description;
          if (!desc) {
            return <span className="text-gray-400">-</span>;
          }
          const truncated = desc.length > 50 ? desc.substring(0, 50) + '...' : desc;
          return (
            <span className="text-gray-700" title={desc}>
              {truncated}
            </span>
          );
        },
      },
      // Status/Tracking fields
      {
        accessorKey: 'claim_code',
        header: 'Claim Code',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{row.original.claim_code}</span>
            <button
              onClick={() => copyCode(row.original.claim_code || '')}
              className="text-gray-400 hover:text-gray-600"
            >
              <ClipboardIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
      {
        accessorKey: 'claim_code_expires_at',
        header: 'Code Expires',
        cell: ({ row }) => {
          const expiresAt = row.original.claim_code_expires_at;
          if (!expiresAt) {
            return <span className="text-gray-400">No expiration</span>;
          }
          const expirationDate = new Date(expiresAt);
          const isExpired = expirationDate < new Date();
          return (
            <span className={isExpired ? 'text-red-600' : 'text-gray-700'}>
              {formatDate(expiresAt)}
            </span>
          );
        },
      },
      {
        accessorKey: 'claim_code_status',
        header: 'Code Status',
        cell: ({ row }) => {
          const status = row.original.claim_code_status;
          const variants: Record<string, 'success' | 'primary' | 'danger' | 'gray'> = {
            active: 'success',
            attempted: 'primary',
            expired: 'danger',
            claimed: 'gray',
          };
          const labels: Record<string, string> = {
            active: 'Active',
            attempted: 'Attempted',
            expired: 'Expired',
            claimed: 'Claimed',
          };
          return (
            <Badge variant={variants[status || 'active']} dot>
              {labels[status || 'active']}
            </Badge>
          );
        },
      },
      {
        id: 'attempts',
        header: 'Attempts',
        cell: ({ row }) => {
          const count = row.original.claim_code_attempt_count || 0;
          if (count === 0) return <span className="text-gray-400">-</span>;
          return (
            <span className="font-medium text-gray-900" title={row.original.claim_code_attempted_at ? `Last: ${formatDate(row.original.claim_code_attempted_at)}` : undefined}>
              {count}
            </span>
          );
        },
      },
      {
        accessorKey: 'is_active',
        header: 'Profile Status',
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? 'success' : 'gray'} dot>
            {row.original.is_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        accessorKey: 'claim_status',
        header: 'Claim Status',
        cell: ({ row }) => (
          <Badge
            variant={row.original.claim_status === 'claimed' ? 'success' : 'warning'}
            dot
          >
            {row.original.claim_status === 'claimed'
              ? `Claimed ${row.original.claimed_at ? formatDate(row.original.claimed_at) : ''}`
              : 'Unclaimed'
            }
          </Badge>
        ),
      },
      {
        accessorKey: 'email_status',
        header: 'Email Status',
        cell: ({ row }) => {
          const status = row.original.email_status;
          const variants: Record<string, 'gray' | 'info' | 'success' | 'warning' | 'danger'> = {
            never_sent: 'gray',
            sent: 'info',
            delivered: 'info',
            opened: 'success',
            clicked: 'success',
            bounced: 'danger',
          };
          const labels: Record<string, string> = {
            never_sent: 'Never Sent',
            sent: 'Sent',
            delivered: 'Delivered',
            opened: 'Opened',
            clicked: 'Clicked',
            bounced: 'Bounced',
          };
          return (
            <Badge variant={variants[status || 'never_sent'] || 'gray'}>
              {labels[status || 'never_sent'] || status}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'invite_clicks',
        header: 'Invites',
        cell: ({ row }) => (
          <span className={row.original.invite_clicks ? 'font-medium text-brand' : 'text-gray-400'}>
            {row.original.invite_clicks || 0}
          </span>
        ),
      },
      {
        accessorKey: 'preset_created_at',
        header: 'Created',
        cell: ({ row }) => formatDate(row.original.preset_created_at),
      },
      // Actions column - Edit button for unclaimed profiles only
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const isUnclaimed = row.original.claim_status === 'unclaimed';
          if (!isUnclaimed) {
            return null;
          }
          return (
            <button
              type="button"
              onClick={() => openEditModal(row.original)}
              className="p-2 text-gray-400 hover:text-brand hover:bg-gray-100 rounded transition-colors"
              title="Edit preset profile"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Preset Profiles</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage preset business profiles with claim codes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={downloadTemplate}
            leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
          >
            Download Template
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowBulkUploadModal(true)}
            leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
          >
            Bulk Upload
          </Button>
          <Button
            onClick={() => { setFormErrors({}); setShowCreateModal(true); }}
            leftIcon={<PlusIcon className="w-4 h-4" />}
          >
            Create Profile
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Total Presets</p>
            <p className="text-2xl font-bold">{stats?.total || 0}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Unclaimed</p>
            <p className="text-2xl font-bold text-orange-600">{stats?.unclaimed || 0}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Claimed</p>
            <p className="text-2xl font-bold text-green-600">{stats?.claimed || 0}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Claim Rate</p>
            <p className="text-2xl font-bold">{stats?.claimRate || 0}%</p>
          </CardBody>
        </Card>
      </div>

      {/* Filters and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="all">All Profiles</option>
              <option value="unclaimed">Unclaimed Only</option>
              <option value="claimed">Claimed Only</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Search by business name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand w-64"
          />

          {/* Email on activation toggle */}
          <div className="flex items-center gap-2 pl-4 border-l border-gray-300">
            <div className="relative inline-flex items-center">
              <input
                type="checkbox"
                id="send-email-on-activation-toggle"
                name="send_email_on_activation_toggle"
                checked={sendEmailOnActivation}
                onChange={(e) => {
                  setSendEmailOnActivation(e.target.checked);
                  localStorage.setItem('presetProfiles_sendEmailOnActivation', e.target.checked.toString());
                }}
                className="sr-only peer"
                aria-label="Send email on activation"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
            </div>
            <div>
              <label
                htmlFor="send-email-on-activation-toggle"
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                Send email on activation
              </label>
              <p className="text-xs text-gray-500" id="send-email-on-activation-toggle-description">
                Auto-send claim code email when profiles are first activated
              </p>
            </div>
          </div>
        </div>
        {selectedProfiles.length > 0 && (
          <div className="flex items-center gap-6">
            {/* Dangerous actions group */}
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => bulkDeactivateMutation.mutate(selectedProfiles)}
                leftIcon={<XCircleIcon className="w-4 h-4" />}
                disabled={bulkDeactivateMutation.isPending}
              >
                {bulkDeactivateMutation.isPending ? 'Deactivating...' : `Deactivate Profile (${selectedProfiles.length})`}
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteClick}
                leftIcon={<TrashIcon className="w-4 h-4" />}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting...' : `Delete (${selectedProfiles.length})`}
              </Button>
            </div>

            {/* Visual separator */}
            <div className="h-8 w-px bg-gray-300" />

            {/* Safe actions group */}
            <div className="flex items-center gap-2">
              <Button
                variant="success"
                onClick={() => handleActivateClick(selectedProfiles)}
                leftIcon={<CheckCircleIcon className="w-4 h-4" />}
                disabled={bulkActivateMutation.isPending}
              >
                {bulkActivateMutation.isPending ? 'Activating...' : `Activate Profile (${selectedProfiles.length})`}
              </Button>
              <Button
                onClick={() => setShowEmailModal(true)}
                leftIcon={<EnvelopeIcon className="w-4 h-4" />}
              >
                Send Email ({selectedProfiles.length})
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Profiles table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={data?.profiles || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={data?.total}
            onPageChange={setPage}
            onRowSelectionChange={(rows) => {
              setSelectedProfiles(rows.map(r => r.original.business_id));
            }}
          />
        </CardBody>
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Preset Profile"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor={createFormIds.businessName} className="block text-sm font-medium text-gray-700 mb-1">
              Business Name *
            </label>
            <input
              id={createFormIds.businessName}
              name="business_name"
              type="text"
              aria-label="Business Name"
              value={formData.business_name}
              onChange={(e) => { setFormData({ ...formData, business_name: e.target.value }); clearFieldError('business_name'); }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand ${formErrors.business_name ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
            />
            {formErrors.business_name && <p className="text-xs text-red-500 mt-1">{formErrors.business_name}</p>}
          </div>

          {/* Structured Industry Fields - Cascading Dropdowns */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={createFormIds.profession} className="block text-sm font-medium text-gray-700 mb-1">
                Profession *
              </label>
              <SearchableSelect
                id={createFormIds.profession}
                name="profession"
                aria-label="Profession"
                value={formData.profession}
                onChange={(newProfession) => {
                  const updates: any = {
                    ...formData,
                    profession: newProfession,
                    focus_types: [],
                  };
                  // Auto-populate industry from profession
                  if (newProfession) {
                    const industries = getIndustriesForProfession(newProfession);
                    if (industries.length === 1) {
                      updates.industry_category = industries[0];
                      clearFieldError('industry_category');
                    }
                  }
                  setFormData(updates);
                  clearFieldError('profession');
                }}
                options={availableProfessions}
                placeholder="Select Profession..."
                hasError={!!formErrors.profession}
              />
              {formErrors.profession && <p className="text-xs text-red-500 mt-1">{formErrors.profession}</p>}
            </div>
            <div>
              <label htmlFor={createFormIds.industryCategory} className="block text-sm font-medium text-gray-700 mb-1">
                Industry Category *
              </label>
              <select
                id={createFormIds.industryCategory}
                name="industry_category"
                aria-label="Industry Category"
                value={formData.industry_category}
                onChange={(e) => { setFormData({
                  ...formData,
                  industry_category: e.target.value,
                  focus_types: [],
                }); clearFieldError('industry_category'); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand ${formErrors.industry_category ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              >
                <option value="">Select Industry...</option>
                {availableIndustries.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
              {formErrors.industry_category && <p className="text-xs text-red-500 mt-1">{formErrors.industry_category}</p>}
            </div>
          </div>

          {/* Focus Types Multi-Select (only shown when profession has focus types) */}
          {hasFocusTypes && (
            <div>
              <p className="block text-sm font-medium text-gray-700 mb-1">
                Specializations <span className="text-gray-400 font-normal">(Optional, Multi-Select)</span>
              </p>
              <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {availableFocusTypes.map((focusType) => {
                    const focusTypeId = `create-focus-type-${focusType.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`;
                    return (
                    <label
                      htmlFor={focusTypeId}
                      key={focusType}
                      className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded"
                    >
                      <input
                        id={focusTypeId}
                        name="focus_types"
                        type="checkbox"
                        checked={formData.focus_types.includes(focusType)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              focus_types: [...formData.focus_types, focusType],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              focus_types: formData.focus_types.filter((ft) => ft !== focusType),
                            });
                          }
                        }}
                        className="rounded border-gray-300 text-brand focus:ring-brand"
                        aria-label={`Specialization ${focusType}`}
                      />
                      <span className="text-sm text-gray-700">{focusType}</span>
                    </label>
                    );
                  })}
                </div>
              </div>
              {formData.focus_types.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {formData.focus_types.map((ft) => (
                    <span
                      key={ft}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-brand/10 text-brand text-xs rounded-full"
                    >
                      {ft}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          focus_types: formData.focus_types.filter((f) => f !== ft),
                        })}
                        className="hover:text-brand/70"
                        aria-label={`Remove specialization ${ft}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label htmlFor={createFormIds.contactEmail} className="block text-sm font-medium text-gray-700 mb-1">
              Contact Email *
            </label>
            <input
              id={createFormIds.contactEmail}
              name="contact_email"
              type="email"
              aria-label="Contact Email"
              value={formData.contact_email}
              onChange={(e) => { setFormData({ ...formData, contact_email: e.target.value }); clearFieldError('contact_email'); }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand ${formErrors.contact_email ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
              placeholder="name@example.com"
            />
            {formErrors.contact_email && <p className="text-xs text-red-500 mt-1">{formErrors.contact_email}</p>}
          </div>

          <div>
            <label htmlFor={createFormIds.address} className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              id={createFormIds.address}
              name="address"
              type="text"
              aria-label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor={createFormIds.city} className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                id={createFormIds.city}
                name="city"
                type="text"
                aria-label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div>
              <label htmlFor={createFormIds.state} className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                id={createFormIds.state}
                name="state"
                type="text"
                aria-label="State"
                value={formData.state}
                onChange={(e) => { setFormData({ ...formData, state: e.target.value.toUpperCase() }); clearFieldError('state'); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand ${formErrors.state ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                maxLength={2}
                placeholder="NJ"
              />
              {formErrors.state && <p className="text-xs text-red-500 mt-1">{formErrors.state}</p>}
            </div>
            <div>
              <label htmlFor={createFormIds.zipCode} className="block text-sm font-medium text-gray-700 mb-1">
                ZIP
              </label>
              <input
                id={createFormIds.zipCode}
                name="zip_code"
                type="text"
                aria-label="ZIP Code"
                value={formData.zip_code}
                onChange={(e) => { setFormData({ ...formData, zip_code: e.target.value }); clearFieldError('zip_code'); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand ${formErrors.zip_code ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                placeholder="07307"
              />
              {formErrors.zip_code && <p className="text-xs text-red-500 mt-1">{formErrors.zip_code}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={createFormIds.phone} className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                id={createFormIds.phone}
                name="phone"
                type="tel"
                aria-label="Phone"
                value={formData.phone}
                onChange={(e) => { setFormData({ ...formData, phone: formatPhoneInput(e.target.value) }); clearFieldError('phone'); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand ${formErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                placeholder="(555) 123-4567"
              />
              {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>}
            </div>
            <div>
              <p className="block text-sm font-medium text-gray-700 mb-1">
                Location Type <span className="text-gray-400 font-normal">(Multi-Select)</span>
              </p>
              <div className="flex flex-wrap gap-3">
                {availableLocationTypes.map((locType) => {
                  const locationTypeId = `${createFormIds.locationType}-${locType.value}`;
                  return (
                  <label
                    htmlFor={locationTypeId}
                    key={locType.value}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      id={locationTypeId}
                      name="location_types"
                      type="checkbox"
                      checked={formData.location_types.includes(locType.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            location_types: [...formData.location_types, locType.value],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            location_types: formData.location_types.filter((lt) => lt !== locType.value),
                          });
                        }
                      }}
                      className="rounded border-gray-300 text-brand focus:ring-brand"
                      aria-label={`Location type ${locType.label}`}
                    />
                    <span className="text-sm text-gray-700">{locType.label}</span>
                  </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <label htmlFor={createFormIds.website} className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              id={createFormIds.website}
              name="website"
              type="url"
              aria-label="Website"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="https://www.example.com"
            />
          </div>

          {/* Service Coverage Area - Only shown when Outbound Service is selected */}
          {formData.location_types.includes('outbound_service') && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Service Coverage Area
              </h4>
              <p className="text-xs text-gray-500 mb-3">
                Define where you travel to provide services
              </p>

              <div>
                <label htmlFor={createFormIds.coverageType} className="block text-sm font-medium text-gray-700 mb-1">
                  Coverage Type
                </label>
                <select
                  id={createFormIds.coverageType}
                  name="coverage_type"
                  aria-label="Coverage Type"
                  value={formData.coverage_type}
                  onChange={(e) => setFormData({ ...formData, coverage_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="">Select coverage type...</option>
                  {availableCoverageTypes.map((ct) => (
                    <option key={ct.value} value={ct.value}>
                      {ct.label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.coverage_type === 'local' && (
                <div className="mt-3">
                  <label htmlFor={createFormIds.coverageRadius} className="block text-sm font-medium text-gray-700 mb-1">
                    Service Radius (miles)
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id={createFormIds.coverageRadius}
                      name="coverage_radius"
                      type="number"
                      aria-label="Service Radius in miles"
                      value={formData.coverage_radius}
                      onChange={(e) => setFormData({ ...formData, coverage_radius: e.target.value })}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      min="1"
                      max="500"
                    />
                    <div className="flex flex-wrap gap-1">
                      {radiusOptions.map((radius) => (
                        <button
                          key={radius}
                          type="button"
                          onClick={() => setFormData({ ...formData, coverage_radius: radius.toString() })}
                          className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                            formData.coverage_radius === radius.toString()
                              ? 'bg-brand text-white border-brand'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-brand'
                          }`}
                        >
                          {radius} mi
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-3">
                <label htmlFor={createFormIds.coverageDetails} className="block text-sm font-medium text-gray-700 mb-1">
                  Coverage Details
                </label>
                <input
                  id={createFormIds.coverageDetails}
                  name="coverage_details"
                  type="text"
                  aria-label="Coverage Details"
                  value={formData.coverage_details}
                  onChange={(e) => setFormData({ ...formData, coverage_details: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  placeholder="e.g., Austin metro area, Travis and Williamson counties"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Describe the areas you serve
                </p>
              </div>
            </div>
          )}

          <div>
            <label htmlFor={createFormIds.description} className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id={createFormIds.description}
              name="description"
              aria-label="Description"
              value={formData.description}
              onChange={(e) => {
                if (e.target.value.length <= 750) {
                  setFormData({ ...formData, description: e.target.value });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              rows={3}
              maxLength={750}
              placeholder="Describe the business, products, and services"
            />
            <div className="flex justify-between items-center mt-1">
              <span className={`text-xs ${
                formData.description.length >= 750
                  ? 'text-red-500'
                  : formData.description.length > 700
                    ? 'text-orange-500'
                    : 'text-gray-400'
              }`}>
                {formData.description.length}/750
              </span>
              {formData.description.length > 650 && formData.description.length < 750 && (
                <span className="text-xs text-gray-400">
                  {750 - formData.description.length} characters remaining
                </span>
              )}
            </div>
          </div>

          {/* Create as Active toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-700">Create as Active</p>
              <p className="text-xs text-gray-500">
                {formData.is_active
                  ? (sendEmailOnActivation ? 'Claim code email will be sent immediately' : 'Profile will be searchable immediately')
                  : 'Profile will be inactive until manually activated'}
              </p>
            </div>
            <label htmlFor={createFormIds.isActive} className="relative inline-flex items-center cursor-pointer">
              <input
                id={createFormIds.isActive}
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="sr-only peer"
                aria-label="Create profile as active"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
            </label>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (validateForm()) {
                createMutation.mutate(false);
              }
            }}
            loading={createMutation.isPending}
          >
            Create Profile
          </Button>
        </ModalFooter>
      </Modal>

      {/* Duplicate Confirmation Modal */}
      <Modal
        isOpen={!!duplicateConflict}
        onClose={() => setDuplicateConflict(null)}
        title="Potential Duplicate Found"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                A profile with the same business name or email already exists.
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                Review the existing profile(s) below and confirm if you still want to create this preset.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Existing matching profile(s):</p>
            {duplicateConflict?.duplicates.map((dup) => (
              <div key={dup.business_id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{dup.business_name}</p>
                    <p className="text-gray-600">{dup.contact_email}</p>
                  </div>
                  <Badge variant={dup.claim_status === 'claimed' ? 'success' : 'warning'}>
                    {dup.claim_status}
                  </Badge>
                </div>
                <p className="text-gray-400 text-xs mt-1">ID: {dup.business_id}</p>
              </div>
            ))}
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>New profile:</strong> {formData.business_name} ({formData.contact_email})
            </p>
          </div>
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={() => setDuplicateConflict(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setDuplicateConflict(null);
              createMutation.mutate(true);
            }}
            loading={createMutation.isPending}
          >
            Create Anyway
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingProfile(null);
        }}
        title="Edit Preset Profile"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor={editFormIds.businessName} className="block text-sm font-medium text-gray-700 mb-1">
              Business Name *
            </label>
            <input
              id={editFormIds.businessName}
              name="business_name"
              type="text"
              aria-label="Business Name"
              value={editFormData.business_name}
              onChange={(e) => setEditFormData({ ...editFormData, business_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          {/* Structured Industry Fields - Cascading Dropdowns */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={editFormIds.profession} className="block text-sm font-medium text-gray-700 mb-1">
                Profession *
              </label>
              <SearchableSelect
                id={editFormIds.profession}
                name="profession"
                aria-label="Profession"
                value={editFormData.profession}
                onChange={(newProfession) => {
                  const updates = {
                    ...editFormData,
                    profession: newProfession,
                    focus_types: [] as string[],
                  };
                  // Auto-populate industry from profession
                  if (newProfession) {
                    const industries = getIndustriesForProfession(newProfession);
                    if (industries.length === 1) {
                      updates.industry_category = industries[0];
                    }
                  }
                  setEditFormData(updates);
                }}
                options={editAvailableProfessions}
                placeholder="Select Profession..."
              />
            </div>
            <div>
              <label htmlFor={editFormIds.industryCategory} className="block text-sm font-medium text-gray-700 mb-1">
                Industry Category *
              </label>
              <select
                id={editFormIds.industryCategory}
                name="industry_category"
                aria-label="Industry Category"
                value={editFormData.industry_category}
                onChange={(e) => setEditFormData({
                  ...editFormData,
                  industry_category: e.target.value,
                  focus_types: [],
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <option value="">Select Industry...</option>
                {availableIndustries.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Focus Types Multi-Select */}
          {editHasFocusTypes && (
            <div>
              <p className="block text-sm font-medium text-gray-700 mb-1">
                Specializations <span className="text-gray-400 font-normal">(Optional, Multi-Select)</span>
              </p>
              <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {editAvailableFocusTypes.map((focusType) => {
                    const focusTypeId = `edit-focus-type-${focusType.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`;
                    return (
                    <label
                      htmlFor={focusTypeId}
                      key={focusType}
                      className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded"
                    >
                      <input
                        id={focusTypeId}
                        name="focus_types"
                        type="checkbox"
                        checked={editFormData.focus_types.includes(focusType)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditFormData({
                              ...editFormData,
                              focus_types: [...editFormData.focus_types, focusType],
                            });
                          } else {
                            setEditFormData({
                              ...editFormData,
                              focus_types: editFormData.focus_types.filter((ft) => ft !== focusType),
                            });
                          }
                        }}
                        className="rounded border-gray-300 text-brand focus:ring-brand"
                        aria-label={`Specialization ${focusType}`}
                      />
                      <span className="text-sm text-gray-700">{focusType}</span>
                    </label>
                    );
                  })}
                </div>
              </div>
              {editFormData.focus_types.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {editFormData.focus_types.map((ft) => (
                    <span
                      key={ft}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-brand/10 text-brand text-xs rounded-full"
                    >
                      {ft}
                      <button
                        type="button"
                        onClick={() => setEditFormData({
                          ...editFormData,
                          focus_types: editFormData.focus_types.filter((f) => f !== ft),
                        })}
                        className="hover:text-brand/70"
                        aria-label={`Remove specialization ${ft}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label htmlFor={editFormIds.contactEmail} className="block text-sm font-medium text-gray-700 mb-1">
              Contact Email *
            </label>
            <input
              id={editFormIds.contactEmail}
              name="contact_email"
              type="email"
              aria-label="Contact Email"
              value={editFormData.contact_email}
              onChange={(e) => setEditFormData({ ...editFormData, contact_email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          <div>
            <label htmlFor={editFormIds.address} className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              id={editFormIds.address}
              name="address"
              type="text"
              aria-label="Address"
              value={editFormData.address}
              onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor={editFormIds.city} className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                id={editFormIds.city}
                name="city"
                type="text"
                aria-label="City"
                value={editFormData.city}
                onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div>
              <label htmlFor={editFormIds.state} className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                id={editFormIds.state}
                name="state"
                type="text"
                aria-label="State"
                value={editFormData.state}
                onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                maxLength={2}
              />
            </div>
            <div>
              <label htmlFor={editFormIds.zipCode} className="block text-sm font-medium text-gray-700 mb-1">
                ZIP
              </label>
              <input
                id={editFormIds.zipCode}
                name="zip_code"
                type="text"
                aria-label="ZIP Code"
                value={editFormData.zip_code}
                onChange={(e) => setEditFormData({ ...editFormData, zip_code: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor={editFormIds.phone} className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                id={editFormIds.phone}
                name="phone"
                type="tel"
                aria-label="Phone"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: formatPhoneInput(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div>
              <p className="block text-sm font-medium text-gray-700 mb-1">
                Location Type <span className="text-gray-400 font-normal">(Multi-Select)</span>
              </p>
              <div className="flex flex-wrap gap-3">
                {availableLocationTypes.map((locType) => {
                  const locationTypeId = `${editFormIds.locationType}-${locType.value}`;
                  return (
                  <label
                    htmlFor={locationTypeId}
                    key={locType.value}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      id={locationTypeId}
                      name="location_types"
                      type="checkbox"
                      checked={editFormData.location_types.includes(locType.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditFormData({
                            ...editFormData,
                            location_types: [...editFormData.location_types, locType.value],
                          });
                        } else {
                          setEditFormData({
                            ...editFormData,
                            location_types: editFormData.location_types.filter((lt) => lt !== locType.value),
                          });
                        }
                      }}
                      className="rounded border-gray-300 text-brand focus:ring-brand"
                      aria-label={`Location type ${locType.label}`}
                    />
                    <span className="text-sm text-gray-700">{locType.label}</span>
                  </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <label htmlFor={editFormIds.website} className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              id={editFormIds.website}
              name="website"
              type="url"
              aria-label="Website"
              value={editFormData.website}
              onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="https://www.example.com"
            />
          </div>

          {/* Service Coverage Area - Only shown when Outbound Service is selected */}
          {editFormData.location_types.includes('outbound_service') && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Service Coverage Area
              </h4>
              <p className="text-xs text-gray-500 mb-3">
                Define where you travel to provide services
              </p>

              <div>
                <label htmlFor={editFormIds.coverageType} className="block text-sm font-medium text-gray-700 mb-1">
                  Coverage Type
                </label>
                <select
                  id={editFormIds.coverageType}
                  name="coverage_type"
                  aria-label="Coverage Type"
                  value={editFormData.coverage_type}
                  onChange={(e) => setEditFormData({ ...editFormData, coverage_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="">Select coverage type...</option>
                  {availableCoverageTypes.map((ct) => (
                    <option key={ct.value} value={ct.value}>
                      {ct.label}
                    </option>
                  ))}
                </select>
              </div>

              {editFormData.coverage_type === 'local' && (
                <div className="mt-3">
                  <label htmlFor={editFormIds.coverageRadius} className="block text-sm font-medium text-gray-700 mb-1">
                    Service Radius (miles)
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id={editFormIds.coverageRadius}
                      name="coverage_radius"
                      type="number"
                      aria-label="Service Radius in miles"
                      value={editFormData.coverage_radius}
                      onChange={(e) => setEditFormData({ ...editFormData, coverage_radius: e.target.value })}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      min="1"
                      max="500"
                    />
                    <div className="flex flex-wrap gap-1">
                      {radiusOptions.map((radius) => (
                        <button
                          key={radius}
                          type="button"
                          onClick={() => setEditFormData({ ...editFormData, coverage_radius: radius.toString() })}
                          className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                            editFormData.coverage_radius === radius.toString()
                              ? 'bg-brand text-white border-brand'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-brand'
                          }`}
                        >
                          {radius} mi
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-3">
                <label htmlFor={editFormIds.coverageDetails} className="block text-sm font-medium text-gray-700 mb-1">
                  Coverage Details
                </label>
                <input
                  id={editFormIds.coverageDetails}
                  name="coverage_details"
                  type="text"
                  aria-label="Coverage Details"
                  value={editFormData.coverage_details}
                  onChange={(e) => setEditFormData({ ...editFormData, coverage_details: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  placeholder="e.g., Austin metro area, Travis and Williamson counties"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Describe the areas you serve
                </p>
              </div>
            </div>
          )}

          <div>
            <label htmlFor={editFormIds.description} className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id={editFormIds.description}
              name="description"
              aria-label="Description"
              value={editFormData.description}
              onChange={(e) => {
                if (e.target.value.length <= 750) {
                  setEditFormData({ ...editFormData, description: e.target.value });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              rows={3}
              maxLength={750}
              placeholder="Describe the business, products, and services"
            />
            <div className="flex justify-between items-center mt-1">
              <span className={`text-xs ${
                editFormData.description.length >= 750
                  ? 'text-red-500'
                  : editFormData.description.length > 700
                    ? 'text-orange-500'
                    : 'text-gray-400'
              }`}>
                {editFormData.description.length}/750
              </span>
              {editFormData.description.length > 650 && editFormData.description.length < 750 && (
                <span className="text-xs text-gray-400">
                  {750 - editFormData.description.length} characters remaining
                </span>
              )}
            </div>
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              setEditingProfile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => updateMutation.mutate()}
            loading={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        isOpen={showBulkUploadModal}
        onClose={() => {
          setShowBulkUploadModal(false);
          setCsvData([]);
          setCsvErrors([]);
        }}
        title="Bulk Upload Profiles"
        size="xl"
      >
        <div className="space-y-4">
          {csvData.length === 0 ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                id="preset-profile-csv-upload"
                name="preset_profile_csv_upload"
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                aria-label="Upload preset profiles CSV file"
              />
              <ArrowUpTrayIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">Upload a CSV file with preset profiles</p>
              <Button onClick={() => fileInputRef.current?.click()}>
                Select File
              </Button>
              <p className="mt-4 text-sm text-gray-500">
                Don't have a template?{' '}
                <button onClick={downloadTemplate} className="text-brand hover:underline">
                  Download it here
                </button>
              </p>
            </div>
          ) : (
            <>
              <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg">
                {csvData.length} profiles ready to import
                {csvErrors.length > 0 && (
                  <span className="text-red-600 ml-2">
                    ({csvErrors.length} with errors)
                  </span>
                )}
              </div>

              {csvErrors.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg max-h-40 overflow-y-auto">
                  <h4 className="font-medium text-red-700 mb-2">Validation Errors:</h4>
                  {csvErrors.map((err, i) => (
                    <div key={i} className="text-sm text-red-600">
                      Row {err.row}: {err.errors.join(', ')}
                    </div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto max-h-60 border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Business</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Industry</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">City</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {csvData.slice(0, 10).map((row, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{row.business_name}</td>
                        <td className="px-3 py-2">{row.industry}</td>
                        <td className="px-3 py-2">{row.contact_email}</td>
                        <td className="px-3 py-2">{row.city}</td>
                      </tr>
                    ))}
                    {csvData.length > 10 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-2 text-gray-500 text-center">
                          ... and {csvData.length - 10} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Info about is_active column in CSV */}
          {csvData.length > 0 && !importProgress.isProcessing && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Tip:</strong> Use the <code className="bg-blue-100 px-1 rounded">is_active</code> column in your CSV to control which profiles are created as active or inactive.
                {sendEmailOnActivation && ' Welcome emails will be sent to profiles marked as active.'}
              </p>
            </div>
          )}

          {/* Import Progress Bar */}
          {importProgress.isProcessing && (
            <div className="space-y-3 p-4 bg-brand-50 rounded-lg border border-brand-200">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-brand-700">
                  Importing profiles...
                </span>
                <span className="text-brand-600">
                  {importProgress.processedCount} / {importProgress.totalCount} ({importProgress.progress}%)
                </span>
              </div>
              <div className="w-full bg-brand-100 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-brand-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${importProgress.progress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-brand-600">
                <span className="flex items-center gap-1">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  {importProgress.successCount} successful
                </span>
                {importProgress.errorCount > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="w-4 h-4 text-red-500" />
                    {importProgress.errorCount} failed
                  </span>
                )}
              </div>
              <p className="text-xs text-brand-500 text-center">
                Please keep this window open. Large imports may take several minutes.
              </p>
            </div>
          )}
        </div>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowBulkUploadModal(false);
              setCsvData([]);
              setCsvErrors([]);
            }}
            disabled={importProgress.isProcessing}
          >
            Cancel
          </Button>
          {csvData.length > 0 && (
            <Button
              onClick={() => bulkCreateMutation.mutate()}
              loading={bulkCreateMutation.isPending}
              disabled={csvErrors.length > 0 || importProgress.isProcessing}
            >
              {importProgress.isProcessing
                ? `Processing... ${importProgress.progress}%`
                : `Import ${csvData.length} Profiles`}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Send Email Modal */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="Send Welcome Emails"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Send welcome emails to {selectedProfiles.length} selected profiles
          </p>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="testMode"
              name="test_mode"
              checked={testMode}
              onChange={(e) => setTestMode(e.target.checked)}
              className="rounded border-gray-300"
              aria-label="Enable test mode for welcome emails"
            />
            <label htmlFor="testMode" className="text-sm text-gray-700">
              Test Mode (send all emails to test address)
            </label>
          </div>

          {testMode && (
            <div>
              <label htmlFor="test-email-address" className="block text-sm font-medium text-gray-700 mb-1">
                Test Email Address
              </label>
              <input
                id="test-email-address"
                name="test_email"
                type="email"
                aria-label="Test Email Address"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                placeholder="your@email.com"
              />
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => sendEmailMutation.mutate(selectedProfiles)}
            loading={sendEmailMutation.isPending}
            disabled={testMode && !testEmail}
          >
            {testMode ? 'Send Test Emails' : 'Send Emails'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Email Confirmation Modal - Requires password to send emails on activation */}
      <Modal
        isOpen={showEmailConfirmModal}
        onClose={() => {
          setShowEmailConfirmModal(false);
          setEmailConfirmPassword('');
          setEmailConfirmError('');
          setPendingActivationIds([]);
        }}
        title="Confirm Email Send"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <EnvelopeIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800">Emails Will Be Sent</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Activating these profiles will send welcome emails with claim codes to{' '}
                  <strong>{getEmailActivationInfo(pendingActivationIds).willReceiveEmailCount}</strong>{' '}
                  business{getEmailActivationInfo(pendingActivationIds).willReceiveEmailCount !== 1 ? 'es' : ''}.
                </p>
                <p className="text-sm text-blue-700 mt-2">
                  Total profiles to activate: <strong>{pendingActivationIds.length}</strong>
                </p>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="email-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
              Enter your password to confirm sending emails
            </label>
            <input
              id="email-confirm-password"
              name="email_confirm_password"
              type="password"
              aria-label="Email confirmation password"
              value={emailConfirmPassword}
              onChange={(e) => {
                setEmailConfirmPassword(e.target.value);
                setEmailConfirmError('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="Your password"
              autoComplete="current-password"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && emailConfirmPassword) {
                  handleEmailConfirmedActivation();
                }
              }}
            />
            {emailConfirmError && (
              <p className="text-sm text-red-600 mt-1">{emailConfirmError}</p>
            )}
          </div>

          <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            <p>
              <strong>Alternative:</strong> You can activate profiles without sending emails by clicking "Activate Without Emails" below.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEmailConfirmModal(false);
              setEmailConfirmPassword('');
              setEmailConfirmError('');
              setPendingActivationIds([]);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleActivateWithoutEmails}
            disabled={emailConfirmLoading}
          >
            Activate Without Emails
          </Button>
          <Button
            variant="success"
            onClick={handleEmailConfirmedActivation}
            loading={emailConfirmLoading}
            disabled={!emailConfirmPassword || emailConfirmLoading}
            leftIcon={<EnvelopeIcon className="w-4 h-4" />}
          >
            Confirm & Send Emails
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletePassword('');
          setDeleteStep('check');
          setDeleteError('');
        }}
        title="Delete Preset Profiles"
      >
        <div className="space-y-4">
          {/* Step 1: Check - Cannot delete claimed profiles */}
          {deleteStep === 'check' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-800">Cannot Delete Claimed Profiles</h4>
                  <p className="text-sm text-red-700 mt-1">
                    {getDeleteValidation().claimedCount} of the selected profiles have been claimed by business owners.
                    Claimed profiles cannot be deleted.
                  </p>
                  <p className="text-sm text-red-700 mt-2">
                    Please deselect the claimed profiles and try again.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Warning - Emails have been sent */}
          {deleteStep === 'warning' && (
            <>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Warning: Emails Already Sent</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      {getDeleteValidation().emailSentCount} of the selected profiles have already received welcome emails.
                      Deleting these profiles will invalidate the claim codes sent to these businesses.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-gray-600">
                Are you sure you want to continue with deletion?
              </p>
            </>
          )}

          {/* Step 3: Confirm - Password verification */}
          {deleteStep === 'confirm' && (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <TrashIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-800">Confirm Deletion</h4>
                    <p className="text-sm text-red-700 mt-1">
                      You are about to permanently delete {selectedProfiles.length} preset profile{selectedProfiles.length !== 1 ? 's' : ''}.
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="delete-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter your password to confirm
                </label>
                <input
                  id="delete-confirm-password"
                  name="delete_confirm_password"
                  type="password"
                  aria-label="Delete confirmation password"
                  value={deletePassword}
                  onChange={(e) => {
                    setDeletePassword(e.target.value);
                    setDeleteError('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Your password"
                  autoComplete="current-password"
                />
                {deleteError && (
                  <p className="text-sm text-red-600 mt-1">{deleteError}</p>
                )}
              </div>
            </>
          )}
        </div>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeletePassword('');
              setDeleteStep('check');
              setDeleteError('');
            }}
          >
            Cancel
          </Button>
          {deleteStep === 'check' && (
            <Button variant="secondary" disabled>
              Cannot Delete
            </Button>
          )}
          {deleteStep === 'warning' && (
            <Button
              variant="danger"
              onClick={() => setDeleteStep('confirm')}
            >
              Continue Anyway
            </Button>
          )}
          {deleteStep === 'confirm' && (
            <Button
              variant="danger"
              onClick={() => deleteMutation.mutate({ businessIds: selectedProfiles, password: deletePassword })}
              loading={deleteMutation.isPending}
              disabled={!deletePassword}
              leftIcon={<TrashIcon className="w-4 h-4" />}
            >
              Delete {selectedProfiles.length} Profile{selectedProfiles.length !== 1 ? 's' : ''}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}
